import { DatasetStringValueMapperDTO } from "components/dashboard/charts/interfaces";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryClient } from "services/HttpService";
import { DatasetMappersApi } from "../DatasetMappersApi";
import {
  defaultMutationOptions,
  defaultResponseHandler,
  IQueryOptions,
  QueryKey,
  useQueryWrapper,
} from "./api-hooks-common";

export const useGetDatasetMappersData = (options?: IQueryOptions) =>
  useQueryWrapper([QueryKey.DATASET_MAPPERS], () => DatasetMappersApi.getAll(), null, {
    ...options,
    staleTime: options?.staleTime ?? 1000 * 60 * 60,
    gcTime: options?.gcTime ?? 1000 * 60 * 60 * 24,
  });

export const useGetDatasetMapperData = (id: number, options?: IQueryOptions) =>
  useQueryWrapper(
    [QueryKey.DATASET_MAPPERS, id.toString()],
    () => DatasetMappersApi.getById(id),
    null,
    {
      ...options,
      staleTime: options?.staleTime ?? 1000 * 60 * 60,
      gcTime: options?.gcTime ?? 1000 * 60 * 60 * 24,
    },
    queryClient.getQueryData([QueryKey.DATASET_MAPPERS, id]) as DatasetStringValueMapperDTO
  );

export const useCreateDatasetMapperData = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DatasetStringValueMapperDTO) => {
      return DatasetMappersApi.create(data).then(defaultResponseHandler);
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.DATASET_MAPPERS], (oldQueryData: DatasetStringValueMapperDTO[]) => {
        return [data, ...oldQueryData];
      });
      options.onSuccess!!();
    },
  });
};

export const useUpdateDatasetMapperData = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DatasetStringValueMapperDTO) => {
      return DatasetMappersApi.update(data.id!, data).then(defaultResponseHandler);
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.DATASET_MAPPERS] });
      queryClient.setQueryData([QueryKey.DATASET_MAPPERS, data.id.toString()], () => {
        return data;
      });
      options.onSuccess!!();
    },
  });
};

export const useDeleteDatasetMapperData = () => {
  const options = { ...defaultMutationOptions };
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: DatasetStringValueMapperDTO) => {
      return DatasetMappersApi.remove(data.id!).then(defaultResponseHandler);
    },
    onError: error => {
      options.onFailure!!(error);
    },
    onMutate: variables => {
      options.onMutate!!(variables);
    },
    onSuccess: data => {
      queryClient.setQueryData([QueryKey.DATASET_MAPPERS], (oldQueryData: DatasetStringValueMapperDTO[]) => {
        const filteredData = oldQueryData.filter((item: DatasetStringValueMapperDTO) => item.id !== data.id);
        return filteredData;
      });
      options.onSuccess!!();
    },
  });
};
